import type { Candidate, Exam, Person, Station } from '../../domain/types';
import { LANGE_TASKS, TASKS } from './content';

/**
 * Prüfungen, Stationen und Personen der Vorlage. Reine Beispieldaten für die Entwicklung —
 * sobald Relay die Fachaktionen anbietet, liefert der Adapter dieselben Formen.
 */

const EXAMINER_NAMES = [
  'Dr. M. Halter',
  'Dr. S. Brunner',
  'PD Dr. A. Kessler',
  'Dr. T. Reinhardt',
  'Prof. Dr. I. Wagner',
  'Dr. K. Lorenz',
  'Dr. F. Steiner',
  'Dr. B. Ammann',
  'Dr. N. Roth',
  'Dr. E. Gerber',
];

const ACTOR_NAMES = [
  'A. Otten',
  'M. Sieber',
  'L. Frei',
  'J. Kunz',
  'P. Marti',
  'S. Baumann',
  'R. Zbinden',
  'C. Hofer',
  'D. Widmer',
  'V. Egger',
];

const people = (count: number, offset: number, prefix: string): Person[] =>
  Array.from({ length: count }, (_, i) => ({
    id: prefix + '-' + i,
    name: EXAMINER_NAMES[(i + offset) % EXAMINER_NAMES.length],
    meta: i % 3 === 0 ? 'angemeldet' : 'verfügbar',
  }));

const actors = (count: number, offset: number, prefix: string): Person[] =>
  Array.from({ length: count }, (_, i) => ({
    id: prefix + '-' + i,
    name: ACTOR_NAMES[(i + offset) % ACTOR_NAMES.length],
    meta: i % 4 === 0 ? 'eingeteilt' : 'verfügbar',
  }));

/** Alle Stationen tragen im Beispiel denselben Bogen — im Echtbetrieb hat jede ihren eigenen. */
const station = (
  id: string,
  label: string,
  meta: string,
  extra: Partial<Station> = {},
): Station => ({
  id,
  label,
  meta,
  durationSeconds: 600,
  tasks: TASKS,
  ...extra,
});

const INNERE_STATIONS: Station[] = [
  station('im-1', '1 — Thoraxschmerz', 'B 2.09'),
  station('im-2', '2 — Aufklärungsgespräch', 'B 2.11'),
  station('im-3', '3 — Herzneurose', 'B 2.14', {
    vignette: {
      teaser:
        'Frau Otten — „Herzanfälle bei unauffälligem internistischem Befund. Psychische Faktoren?"',
      lead:
        'Sie sind diensthabender Arzt in einer internistischen Ambulanz. Zu Ihnen kommt Frau Otten, ' +
        'die von ihrem Hausarzt mit folgender Bitte um Abklärung überwiesen wurde: „Herzanfälle bei ' +
        'unauffälligem internistischem Befund. Psychische Faktoren?"',
      task:
        'Führen Sie eine gezielte Anamnese zur Herausarbeitung eventueller psychosomatischer Aspekte. ' +
        'Ausgiebige internistische Untersuchungen sind bereits erfolgt.',
    },
  }),
  station('im-4', '4 — EKG-Befundung', 'B 2.16'),
  station('im-5', '5 — Abdomenuntersuchung', 'B 2.18'),
  station('im-6', '6 — Dyspnoe', 'B 2.20'),
  station('im-7', '7 — Diabetesberatung', 'B 2.22'),
  station('im-8', '8 — Blutentnahme', 'B 2.24'),
  // ⚠️ **Ans Ende, nicht an den Anfang.** Mehrere Tests greifen auf `stations[0]` zu und
  // erwarten dort die kurze erste Station; eine eingeschobene verschöbe sie alle.
  //
  // Eine bewusst lange Station: fünfzehn Aufgaben. Der Bogenabzug beim Absenden kachelt, und
  // mit vier Aufgaben sind das zwei Kacheln — damit ist der mehrteilige Fall berührt, aber
  // nicht ausgereizt. Hier werden es viele, und erst daran zeigt sich, ob Zeitbedarf und
  // Speicher tragen.
  station('im-lang', '9 — Ganzkörperstatus (lang)', 'B 2.07', {
    tasks: LANGE_TASKS,
    durationSeconds: 1200,
  }),
];

/*
 * Erfundene Prüflinge. `firstName`/`lastName` stehen einzeln, weil die Liste danach sortieren
 * kann; `examNo` ist die laufende Nummer **in dieser Prüfung** — hier bewusst nicht in der
 * Reihenfolge der Namen, damit ein Sortierfehler auffällt.
 */
const CANDIDATES: Candidate[] = [
  { id: '3310', name: 'Mia Ackermann', firstName: 'Mia', lastName: 'Ackermann', matriculationNumber: '3310', examNo: '7', state: 'bewertet', score: 13 },
  { id: '3311', name: 'Sven Brandt', firstName: 'Sven', lastName: 'Brandt', matriculationNumber: '3311', examNo: '3', state: 'bewertet', score: 11.5 },
  { id: '3312', name: 'Lea Wenzel', firstName: 'Lea', lastName: 'Wenzel', matriculationNumber: '3312', examNo: '11', state: 'laeuft', score: null },
  { id: '3313', name: 'Jan Kilic', firstName: 'Jan', lastName: 'Kilic', matriculationNumber: '3313', examNo: '1', state: 'wartet', score: null },
  { id: '3314', name: 'Paul Neumann', firstName: 'Paul', lastName: 'Neumann', matriculationNumber: '3314', examNo: '9', state: 'wartet', score: null },
  { id: '3315', name: 'Rana Sadiq', firstName: 'Rana', lastName: 'Sadiq', matriculationNumber: '3315', examNo: '5', state: 'wartet', score: null },
  { id: '3316', name: 'Tom Hoffmann', firstName: 'Tom', lastName: 'Hoffmann', matriculationNumber: '3316', examNo: '12', state: 'wartet', score: null },
  { id: '3317', name: 'Ada Vogel', firstName: 'Ada', lastName: 'Vogel', matriculationNumber: '3317', examNo: '2', state: 'entschuldigt', score: null },
  { id: '3318', name: 'Carla Berger', firstName: 'Carla', lastName: 'Berger', matriculationNumber: '3318', examNo: '8', state: 'wartet', score: null },
  { id: '3319', name: 'Erik Lindqvist', firstName: 'Erik', lastName: 'Lindqvist', matriculationNumber: '3319', examNo: '4', state: 'wartet', score: null },
  { id: '3320', name: 'Nora Özdemir', firstName: 'Nora', lastName: 'Özdemir', matriculationNumber: '3320', examNo: '10', state: 'wartet', score: null },
  { id: '3321', name: 'Finn Baumgartner', firstName: 'Finn', lastName: 'Baumgartner', matriculationNumber: '3321', examNo: '6', state: 'wartet', score: null },
];

/**
 * Kurzform eines Stationsnamens für die Kennung: klein geschrieben, Umlaute aufgelöst, alles
 * außer Buchstaben und Ziffern zu Bindestrichen. Bewusst aus dem **Inhalt** gebildet, nicht aus
 * der Position in der Liste — eine mittig eingefügte Station darf die Kennungen der übrigen
 * nicht verschieben, sonst schriebe ein erneutes Einlesen den Inhalt der einen Station in die
 * Zeilen einer anderen.
 *
 * ⚠️ Die Kehrseite, ausdrücklich in Kauf genommen: der Schlüssel hängt am **Text**. Wird ein
 * Label umformuliert, ändert sich der Slug und mit ihm kaskadierend die Kennungen von
 * `ExamItemContainer`, `ExamItem`, `ExamQuestion` und `ExamAnswer` dieser Station
 * (`toBundle.ts` leitet sie alle aus `station.id` ab) — ein bereits geschriebener
 * `ExamLog`-Eintrag zeigte danach ins Leere. Das ist derselbe Kompromiss, der bei den
 * `ExamAnswer`-Indexschlüsseln umgekehrt entschieden wurde (dort: Index statt Text, weil eine
 * verschobene Antwortkennung nur *eine* Zeile trifft, keine ganze Kette); hier wiegt die
 * Positionsunabhängigkeit schwerer, weil Stationen eingefügt, aber selten umbenannt werden.
 * Ab Phase 4 ist die Frage erledigt: dann liefert der Server echte Relay-Kennungen und diese
 * Datei fällt weg.
 */
export function slug(label: string): string {
  return label
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Stationen mit Kennung aus dem Label statt aus dem Index — siehe `slug`. Die laufende Nummer
 * steht nur im Anzeigenamen (`label`), nie im Schlüssel: sonst wäre die Positionsabhängigkeit
 * nur verlagert, nicht behoben.
 */
export const namedStations = (prefix: string, labels: string[], room: (i: number) => string) =>
  labels.map((label, i) => station(prefix + '-' + slug(label), i + 1 + ' — ' + label, room(i)));

/**
 * Die **eine** Demo-Pruefung, die der Knopf in den Einstellungen einliest.
 *
 * ⬅️ **Bis zum 2026-09-17 standen hier vier** (Innere, Nachpruefung, Chirurgie,
 * Kommunikation). Befund des Nutzers: „Es ist doch nur eine Demo-Pruefung. Warum sprichst Du
 * in den Settings dann von Pruefungen?" — und er hatte recht. Die vier unterschieden sich nur
 * in Name, Stationsbezeichnung, Raumnummer und Prueflingszahl; **den Bogen trugen alle
 * gleich** (s. `station()` oben). Es waren vier Umschlaege um einen Inhalt, und die Mehrzahl in
 * der Oberflaeche versprach Vielfalt, die es nie gab.
 */
export const EXAMS: Exam[] = [
  {
    id: 'im-ss26',
    name: 'OSCE Innere Medizin · SS 2026',
    meta: '8 Stationen · 42 Prüflinge · geladen 07:04',
    loaded: true,
    stations: INNERE_STATIONS,
    examiners: people(9, 0, 'ex-im'),
    actors: actors(8, 0, 'ac-im'),
    candidates: CANDIDATES,
  },
];

/**
 * Eine zweite Pruefung — **nur fuer Tests**, nicht fuer die Demo.
 *
 * ⚠️ Sie steht bewusst ausserhalb von `EXAMS`: der Demo-Knopf liest sie nicht ein, die
 * Oberflaeche kennt sie nicht. Gebraucht wird sie vom Test des **Pruefungswechsels**
 * (`store/useAppStore.test.ts`) — dass der Aufgabenindex beim Wechsel neu entsteht und nicht
 * der alte stehenbleibt, laesst sich mit einer einzigen Pruefung nicht pruefen.
 */
export const ZWEITE_BEISPIELPRUEFUNG: Exam = {
  id: 'im-nach',
  name: 'OSCE Innere Medizin · Nachprüfung',
  meta: '4 Stationen · 9 Prüflinge · geladen 06:58',
  loaded: true,
  stations: namedStations(
    'nach',
    ['Thoraxschmerz', 'Herzneurose', 'EKG-Befundung', 'Dyspnoe'],
    (i) => 'C 1.0' + (4 + i * 2),
  ),
  examiners: people(4, 3, 'ex-nach'),
  actors: actors(4, 2, 'ac-nach'),
  candidates: CANDIDATES.slice(0, 9),
};
